/* eslint-disable unicorn/prefer-module */
import { useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { CategoriaLeyendaIpsos, Loader } from "..";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaViajes.module.scss";

// =====================================================
// IPSOS PERSONALES (Base: 400 casos)
// =====================================================
const IPSOS_PERSONALES = {
  // factor = viajes por año
  frecuenciaVacaciones: {
    variasVeces: { pct: 0.252, factor: 3, label: "Varias veces en el año" },
    dosVeces: { pct: 0.035, factor: 2, label: "2 veces al año" },
    unaVez: { pct: 0.601, factor: 1, label: "Una vez al año" },
    cadaDosAnos: { pct: 0.096, factor: 0.5, label: "Una vez cada dos años" },
    nunca: { pct: 0.016, factor: 0, label: "Nunca ha tomado vacaciones" },
  },
  aerolineas: { Latam: 0.827, Sky: 0.068, JetSmart: 0.004, "Sin preferencia": 0.1 },
  tipoDestino: { Nacional: 0.548, Internacional: 0.437, Regional: 0.016 },
};

// =====================================================
// IPSOS LABORALES (Base: 400 casos)
// =====================================================
const IPSOS_LABORALES = {
  viajan: 0.3,
  frecuencia: {
    semanal: { pct: 0.174, viajesMes: 4, label: "Semanalmente" },
    mensual: { pct: 0.409, viajesMes: 1, label: "Mensualmente" },
    trimestral: { pct: 0.243, viajesMes: 0.33, label: "Trimestralmente" },
    semestral: { pct: 0.103, viajesMes: 0.17, label: "Semestralmente" },
    anual: { pct: 0.071, viajesMes: 0.083, label: "Anualmente" },
  },
  decisorCompra: {
    "La empresa": 0.503,
    "El viajero": 0.375,
    "El jefe": 0.082,
    Agencia: 0.039,
  },
  hospedaje: {
    Hotel: 0.788,
    Airbnb: 0.111,
    "Casa empresa": 0.043,
    Casa: 0.03,
    Hostal: 0.028,
  },
};

const TEMPORADA_OPCIONES = [
  { key: "Verano", pct: 0.546 },
  { key: "Otoño", pct: 0.204 },
  { key: "Invierno", pct: 0.153 },
  { key: "Primavera", pct: 0.097 },
];

const TICKETS_PERSONALES = [300000, 500000, 1000000, 2000000];
const TICKETS_LABORALES = [80000, 150000, 300000, 600000];

const COLORES_AEROLINEAS = ["#0d6efd", "#6c757d", "#fd7e14", "#198754"];
const COLORES_DESTINO = ["#0d6efd", "#198754", "#fd7e14"];
const COLORES_DECISOR = ["#0d6efd", "#198754", "#fd7e14", "#6c757d"];
const COLORES_HOSPEDAJE = ["#0d6efd", "#198754", "#fd7e14", "#6c757d", "#ffc107"];

const renderPieChart = (data, colors) => {
  let accumulated = 0;
  const segments = data.map(({ share }, i) => {
    const start = accumulated;
    accumulated += share * 100;
    return `${colors[i % colors.length]} ${start}% ${accumulated}%`;
  });
  const gradient = `conic-gradient(${segments.join(", ")})`;

  return (
    <div className={styles.pieChart} style={{ background: gradient }}>
      <div className={styles.pieChartInner} />
    </div>
  );
};

const sortShare = (obj) =>
  Object.entries(obj)
    .sort(([, a], [, b]) => b - a)
    .map(([nombre, share]) => ({ nombre, share }));

export const Viajes = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [verAnual, setVerAnual] = useState(false);
  const [tab, setTab] = useState("personales");

  // Personales
  const [ticketPromedio, setTicketPromedio] = useState(500000);
  const [anoCompleto, setAnoCompleto] = useState(true);
  const [temporadaSeleccionada, setTemporadaSeleccionada] = useState("Verano");

  // Laborales
  const [ticketLaboralPromedio, setTicketLaboralPromedio] = useState(150000);
  const [incluyeHospedaje, setIncluyeHospedaje] = useState(true);

  // =====================================================
  // CÁLCULOS PERSONALES
  // =====================================================
  const resultadosPersonales = useMemo(() => {
    if (!totalEjecutivos) return null;

    const desgloseFrecuencia = {};
    let totalViajesAnuales = 0;

    Object.entries(IPSOS_PERSONALES.frecuenciaVacaciones).forEach(([freq, info]) => {
      const personas = Math.round(totalEjecutivos * info.pct);
      const viajes = Math.round(personas * info.factor);
      desgloseFrecuencia[freq] = { pct: info.pct, label: info.label, personas, viajes };
      totalViajesAnuales += viajes;
    });

    const totalViajesMes = Math.round(totalViajesAnuales / 12);

    const temporadaPct = anoCompleto ? 1 : TEMPORADA_OPCIONES.find((t) => t.key === temporadaSeleccionada)?.pct || 1;
    const mercadoTemporadaAnual = Math.round(totalViajesAnuales * temporadaPct * ticketPromedio);
    const mercadoTemporadaMensual = Math.round(mercadoTemporadaAnual / 12);

    return {
      desgloseFrecuencia,
      totalViajes: totalViajesMes,
      totalViajesAnuales,
      mercadoTemporada: mercadoTemporadaMensual,
      mercadoTemporadaAnual,
      aerolineaData: sortShare(IPSOS_PERSONALES.aerolineas),
      tipoDestinoData: sortShare(IPSOS_PERSONALES.tipoDestino),
    };
  }, [totalEjecutivos, ticketPromedio, anoCompleto, temporadaSeleccionada]);

  // =====================================================
  // CÁLCULOS LABORALES
  // =====================================================
  const resultadosLaborales = useMemo(() => {
    if (!totalEjecutivos) return null;

    const viajanPorTrabajo = Math.round(totalEjecutivos * IPSOS_LABORALES.viajan);

    let totalViajesMes = 0;
    const desgloseFrecuencia = {};

    Object.entries(IPSOS_LABORALES.frecuencia).forEach(([key, info]) => {
      const personas = Math.round(viajanPorTrabajo * info.pct);
      const viajesMes = Math.round(personas * info.viajesMes);
      desgloseFrecuencia[key] = { ...info, personas, viajesMes };
      totalViajesMes += viajesMes;
    });

    const mercadoPasajes = totalViajesMes * ticketLaboralPromedio;
    const mercadoHospedaje = incluyeHospedaje ? Math.round(totalViajesMes * ticketLaboralPromedio * 0.6) : 0;
    const mercadoTotal = mercadoPasajes + mercadoHospedaje;

    return {
      viajanPorTrabajo,
      desgloseFrecuencia,
      totalViajesMes,
      totalViajesAnio: totalViajesMes * 12,
      mercadoTotal,
      mercadoTotalAnual: mercadoTotal * 12,
      decisorData: sortShare(IPSOS_LABORALES.decisorCompra),
      hospedajeData: sortShare(IPSOS_LABORALES.hospedaje),
    };
  }, [totalEjecutivos, ticketLaboralPromedio, incluyeHospedaje]);

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  const frecuenciaKeysPersonales = Object.keys(IPSOS_PERSONALES.frecuenciaVacaciones);
  const frecuenciaKeysLaborales = Object.keys(IPSOS_LABORALES.frecuencia);

  return (
    <div className={styles.viajesPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>VIAJES</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.viajesContent}>
        {/* Tab bar */}
        <div className={styles.tabsBar}>
          <button
            type="button"
            className={cx(styles.tab, tab === "personales" && styles.tabActivo)}
            onClick={() => setTab("personales")}
          >
            PERSONALES
          </button>
          <button
            type="button"
            className={cx(styles.tab, tab === "laborales" && styles.tabActivo)}
            onClick={() => setTab("laborales")}
          >
            LABORALES
          </button>
        </div>

        {/* ========================= TAB PERSONALES ========================= */}
        {tab === "personales" && resultadosPersonales && (
          <>
            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de vacaciones</th>
                    <th className={cx("text-center", styles.thPersonas)}>Según frecuencia</th>
                    <th className={cx("text-center", styles.thConversiones)}>Viajes {verAnual ? "año" : "mes"}</th>
                  </tr>
                </thead>
                <tbody>
                  {frecuenciaKeysPersonales.map((freq) => {
                    const data = resultadosPersonales.desgloseFrecuencia[freq];
                    return (
                      <tr key={freq}>
                        <td className={styles.celdaFrecuencia}>
                          <div className={styles.frecuenciaInner}>
                            <span className={styles.badgeIpsos}>{(data.pct * 100).toFixed(0)}%</span>
                            <span>{data.label}</span>
                          </div>
                        </td>
                        <td className="text-center">{formatNumber(data.personas)}</td>
                        <td className="text-center">
                          {freq === "nunca" ? "-" : formatNumber(verAnual ? data.viajes * 12 : data.viajes)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className={styles.filaTotales}>
                    <td colSpan={2} className={styles.celdaVacia} />
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>
                        {formatNumber(
                          verAnual ? resultadosPersonales.totalViajesAnuales : resultadosPersonales.totalViajes
                        )}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={styles.configuracionRow}>
              <div className={styles.configuracionBloque}>
                <div className={styles.bloqueGris}>
                  <div className={styles.bloqueLabelRow}>
                    <span className={styles.bloqueLabel}>Preferencia por temporada</span>
                    <label className={styles.anoCompletoCheck}>
                      <input type="checkbox" checked={anoCompleto} onChange={(e) => setAnoCompleto(e.target.checked)} />
                      <span>Año completo</span>
                    </label>
                  </div>
                  <div className={cx(styles.temporadaOpciones, anoCompleto && styles.temporadaDisabled)}>
                    {TEMPORADA_OPCIONES.map((t) => (
                      <label key={t.key} className={styles.opcionRadio}>
                        <span className={styles.badgeIpsos}>{(t.pct * 100).toFixed(0)}%</span>
                        <span>{t.key}</span>
                        <input
                          type="radio"
                          name="temporada"
                          disabled={anoCompleto}
                          checked={temporadaSeleccionada === t.key}
                          onChange={() => setTemporadaSeleccionada(t.key)}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.configuracionBloque}>
                <div className={styles.bloqueGris}>
                  <div className={styles.bloqueLabel}>Ticket promedio</div>
                  <div className={styles.ticketOpciones}>
                    {TICKETS_PERSONALES.map((t) => (
                      <label key={t} className={styles.opcionRadio}>
                        <input
                          type="radio"
                          name="ticketPromedio"
                          checked={ticketPromedio === t}
                          onChange={() => setTicketPromedio(t)}
                        />
                        <span>{formatLocal(convertCLP(t))}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.mercadoRow}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>
                  Mercado {verAnual ? "anual" : "mensual"}
                  {!anoCompleto ? ` (${temporadaSeleccionada})` : ""}
                </span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(
                  convertCLP(
                    verAnual ? resultadosPersonales.mercadoTemporadaAnual : resultadosPersonales.mercadoTemporada
                  )
                )}
              </div>
            </div>

            <div className={styles.pieChartsRow}>
              <div className={styles.pieChartContainer}>
                <h4>Aerolínea de preferencia</h4>
                <div className={styles.pieChartContent}>
                  {renderPieChart(resultadosPersonales.aerolineaData, COLORES_AEROLINEAS)}
                  <div className={styles.pieLegend}>
                    {resultadosPersonales.aerolineaData.map((d, i) => (
                      <div key={d.nombre} className={styles.legendItem}>
                        <span className={styles.legendColor} style={{ backgroundColor: COLORES_AEROLINEAS[i] }} />
                        <span className={styles.legendLabel}>{d.nombre}</span>
                        <span className={styles.legendValue}>{(d.share * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.pieChartContainer}>
                <h4>Tipo de destino</h4>
                <div className={styles.pieChartContent}>
                  {renderPieChart(resultadosPersonales.tipoDestinoData, COLORES_DESTINO)}
                  <div className={styles.pieLegend}>
                    {resultadosPersonales.tipoDestinoData.map((d, i) => (
                      <div key={d.nombre} className={styles.legendItem}>
                        <span className={styles.legendColor} style={{ backgroundColor: COLORES_DESTINO[i] }} />
                        <span className={styles.legendLabel}>{d.nombre}</span>
                        <span className={styles.legendValue}>{(d.share * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.disclaimer}>
              <p>
                <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos. El 60.1% toma vacaciones una vez al año.
                Latam concentra el 82.7% de preferencia en aerolíneas.
              </p>
            </div>
          </>
        )}

        {/* ========================= TAB LABORALES ========================= */}
        {tab === "laborales" && resultadosLaborales && (
          <>
            <div className={styles.resumenPenetracion}>
              <div className={styles.resumenCard}>
                <div className={styles.resumenValor}>
                  <span className={styles.badgeIpsos}>{(IPSOS_LABORALES.viajan * 100).toFixed(0)}%</span>
                </div>
                <div className={styles.resumenLabel}>Viajan por trabajo</div>
                <div className={styles.resumenNumero}>
                  {formatNumber(resultadosLaborales.viajanPorTrabajo)} personas
                </div>
              </div>
              <div className={styles.resumenCard}>
                <div className={styles.resumenValor}>
                  <span className={styles.valorDestacado}>
                    {formatNumber(verAnual ? resultadosLaborales.totalViajesAnio : resultadosLaborales.totalViajesMes)}
                  </span>
                </div>
                <div className={styles.resumenLabel}>Viajes estimados / {verAnual ? "año" : "mes"}</div>
                <div className={styles.resumenDetalle}>
                  {formatNumber(verAnual ? resultadosLaborales.totalViajesMes : resultadosLaborales.totalViajesAnio)}{" "}
                  viajes/{verAnual ? "mes" : "año"}
                </div>
              </div>
              <div className={styles.resumenCard}>
                <div className={styles.resumenValor}>
                  <span className={styles.valorDestacado}>
                    {formatLocal(
                      convertCLP(verAnual ? resultadosLaborales.mercadoTotalAnual : resultadosLaborales.mercadoTotal)
                    )}
                  </span>
                </div>
                <div className={styles.resumenLabel}>Mercado viajes</div>
                <div className={styles.resumenDetalle}>
                  {formatLocal(
                    convertCLP(verAnual ? resultadosLaborales.mercadoTotal : resultadosLaborales.mercadoTotalAnual)
                  )}
                  /{verAnual ? "mes" : "año"}
                </div>
              </div>
            </div>

            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia</th>
                    <th className={cx("text-center", styles.thPersonas)}>Viajeros</th>
                    <th className={cx("text-center", styles.thConversiones)}>
                      <div className={styles.headerConsumos}>
                        <span>Viajes</span>
                        <div className={styles.toggleContainer}>
                          <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                          <label className={styles.toggleSwitch}>
                            <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {frecuenciaKeysLaborales.map((key) => {
                    const data = resultadosLaborales.desgloseFrecuencia[key];
                    return (
                      <tr key={key}>
                        <td className={styles.celdaFrecuencia}>
                          <div className={styles.frecuenciaInner}>
                            <span className={styles.badgeIpsos}>{(data.pct * 100).toFixed(0)}%</span>
                            <span>{data.label}</span>
                          </div>
                        </td>
                        <td className="text-center">{formatNumber(data.personas)}</td>
                        <td className="text-center">{formatNumber(verAnual ? data.viajesMes * 12 : data.viajesMes)}</td>
                      </tr>
                    );
                  })}
                  <tr className={styles.filaTotales}>
                    <td colSpan={2} className={styles.celdaVacia} />
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>
                        {formatNumber(
                          verAnual ? resultadosLaborales.totalViajesAnio : resultadosLaborales.totalViajesMes
                        )}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={styles.configuracionRow}>
              <div className={styles.configuracionBloque}>
                <div className={styles.bloqueGris}>
                  <div className={styles.bloqueLabel}>Ticket promedio pasaje</div>
                  <div className={styles.ticketOpciones}>
                    {TICKETS_LABORALES.map((t) => (
                      <label key={t} className={styles.opcionRadio}>
                        <input
                          type="radio"
                          name="ticketLaboralPromedio"
                          checked={ticketLaboralPromedio === t}
                          onChange={() => setTicketLaboralPromedio(t)}
                        />
                        <span>{formatLocal(convertCLP(t))}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.configuracionBloque}>
                <div className={styles.bloqueGris}>
                  <div className={styles.bloqueLabel}>Hospedaje</div>
                  <label className={styles.hospedajeCheck}>
                    <input
                      type="checkbox"
                      checked={incluyeHospedaje}
                      onChange={(e) => setIncluyeHospedaje(e.target.checked)}
                    />
                    <span>Incluir hospedaje en el mercado</span>
                  </label>
                  <div className={styles.hospedajeDetalle}>Hotel promedio: +60% del pasaje</div>
                </div>
              </div>
            </div>

            <div className={styles.mercadoRow}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>
                  Mercado {incluyeHospedaje ? "pasaje + hospedaje" : "pasaje"} {verAnual ? "anual" : "mensual"}
                </span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(
                  convertCLP(verAnual ? resultadosLaborales.mercadoTotalAnual : resultadosLaborales.mercadoTotal)
                )}
              </div>
            </div>

            <div className={styles.pieChartsRow}>
              <div className={styles.pieChartContainer}>
                <h4>¿Quién decide la compra?</h4>
                <div className={styles.pieChartContent}>
                  {renderPieChart(resultadosLaborales.decisorData, COLORES_DECISOR)}
                  <div className={styles.pieLegend}>
                    {resultadosLaborales.decisorData.map((d, i) => (
                      <div key={d.nombre} className={styles.legendItem}>
                        <span className={styles.legendColor} style={{ backgroundColor: COLORES_DECISOR[i] }} />
                        <span className={styles.legendLabel}>{d.nombre}</span>
                        <span className={styles.legendValue}>{(d.share * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.pieChartContainer}>
                <h4>Tipo de hospedaje</h4>
                <div className={styles.pieChartContent}>
                  {renderPieChart(resultadosLaborales.hospedajeData, COLORES_HOSPEDAJE)}
                  <div className={styles.pieLegend}>
                    {resultadosLaborales.hospedajeData.map((d, i) => (
                      <div key={d.nombre} className={styles.legendItem}>
                        <span className={styles.legendColor} style={{ backgroundColor: COLORES_HOSPEDAJE[i] }} />
                        <span className={styles.legendLabel}>{d.nombre}</span>
                        <span className={styles.legendValue}>{(d.share * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.disclaimer}>
              <p>
                <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos. El 30% de los ejecutivos viaja por trabajo.
                Hotel concentra el 78.8% del hospedaje y la empresa decide el 50.3% de las compras.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Viajes;
